import React, { useState, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useBusinesses, Business } from './api/useBusinesses';
import { useDeleteBusiness } from './api/useDeleteBusiness';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';

const CreateBusinessModal = lazy(() => import('./CreateBusinessModal'));
const EditBusinessModal = lazy(() => import('./EditBusinessModal'));
const CreateEventModal = lazy(() => import('../events/CreateEventModal'));
const CreatePushNotificationModal = lazy(() => import('./CreatePushNotificationModal'));
const CheckinQrModal = lazy(() => import('./CheckinQrModal'));

// ─────────────────────────────────────────────────────────────────────────────
// Delete Confirm Modal
// ─────────────────────────────────────────────────────────────────────────────

function DeleteConfirmModal({ business, onClose, onConfirm, loading }: { business: Business; onClose: () => void; onConfirm: () => void; loading: boolean }) {
  const [confirmText, setConfirmText] = useState('');

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col max-h-[92vh] sm:my-4">
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
          <div className="w-12 h-12 mx-auto rounded-xl bg-red-950/50 border border-red-800/50 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">Delete Business</h3>
            <p className="text-sm text-gray-400 mt-1">
              Are you sure you want to delete <span className="text-white font-medium">{business.name}</span>? This action is permanent. Type <span className="text-red-400 font-mono">delete</span> to confirm.
            </p>
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="delete"
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-red-500 transition-all"
            autoFocus
          />
        </div>
        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <button onClick={handleClose} disabled={loading} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={confirmText !== 'delete' || loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <><Spinner size="sm" />Deleting…</> : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Businesses Tab
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export function BusinessesTab({ onRequestCreateDeal }: { onRequestCreateDeal: (business: Business, env: Env) => void }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [page, setPage] = useState(0);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [deletingBusiness, setDeletingBusiness] = useState<Business | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createEventBiz, setCreateEventBiz] = useState<Business | null>(null);
  const [createNotifBiz, setCreateNotifBiz] = useState<Business | null>(null);
  const [checkinQrBiz, setCheckinQrBiz] = useState<Business | null>(null);
const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeApiUrl = getAdminApiUrl(selectedEnv);

  const { businesses, total, loading, error, refetch } = useBusinesses({ search: debouncedSearch, page, limit: PAGE_SIZE, apiUrl: activeApiUrl });
  const { loading: deleteLoading, error: deleteError, deleteBusiness } = useDeleteBusiness();

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setPage(0);
  }, []);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const handleCreated = useCallback(() => {
    setShowCreateModal(false);
    refetch();
    showSuccess('Business created successfully.');
  }, [refetch, showSuccess]);

  const handleEdited = useCallback(() => {
    setEditingBusiness(null);
    refetch();
    showSuccess('Business updated successfully.');
  }, [refetch, showSuccess]);

  const handleCopyId = useCallback(async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      showSuccess(`Copied business ID: ${id}`);
    } catch {
      showSuccess('Failed to copy ID.');
    }
  }, [showSuccess]);

  const handleDelete = useCallback(async () => {
    if (!deletingBusiness) return;
    const ok = await deleteBusiness(deletingBusiness.id, activeApiUrl);
    if (ok) {
      setDeletingBusiness(null);
      if (businesses.length === 1 && page > 0) setPage(p => p - 1);
      else refetch();
      showSuccess('Business deleted successfully.');
    }
  }, [deletingBusiness, deleteBusiness, activeApiUrl, businesses.length, page, refetch, showSuccess]);

  return (
    <>
      {/* Toolbar: Search + Env switcher + Create button */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input type="text" value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by name or email…" className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all" />
        </div>
        {!loading && total > 0 && <span className="text-sm text-gray-500">{total} total</span>}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const envApiUrl = getAdminApiUrl(env);
            const isConfigured = Boolean(envApiUrl);
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {/* PROD badge */}
        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}

        <Link to="/geofences" className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-700 rounded-xl hover:text-white hover:bg-gray-800 transition-all">
          Geofence map
        </Link>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Create Business
        </button>
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {(error || deleteError) && <ErrorBanner message={error || deleteError!} />}

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <div className="w-10 flex-shrink-0" />
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</p>
          <div className="flex-shrink-0 w-40" />
        </div>

        {loading && <div className="py-16 text-center flex flex-col items-center gap-3"><Spinner size="lg" /><p className="text-sm text-gray-500">Loading businesses…</p></div>}
        {!loading && businesses.length === 0 && <div className="py-16 text-center"><p className="text-gray-500 text-sm">{search ? 'No businesses match your search.' : 'No businesses found.'}</p></div>}

        {!loading && businesses.map((biz, i) => (
          <div key={biz.id} className={`group px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 active:bg-gray-800/50 transition-colors ${i < businesses.length - 1 ? 'border-b border-gray-800' : ''}`}>
            {/* Main row */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-700">
                {biz.business_logo
                  ? <img src={biz.business_logo} alt={biz.name} className="w-full h-full object-cover" />
                  : <span className="text-sm font-bold text-gray-400">{biz.name.charAt(0).toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{biz.name}</p>
                <p className="text-xs text-gray-500 truncate">{biz.address || '—'}</p>
              </div>
              <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all">
                <button onClick={() => handleCopyId(biz.id)} title={biz.id} className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-violet-500 hover:bg-gray-700 transition-all">Copy ID</button>
                <button onClick={() => setEditingBusiness(biz)} className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-violet-500 hover:bg-gray-700 transition-all">Edit</button>
                <button onClick={() => setCreateEventBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg hover:text-white hover:border-violet-500 hover:bg-violet-900/40 transition-all">Create Event</button>
                <button onClick={() => onRequestCreateDeal(biz, selectedEnv)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg hover:text-white hover:border-violet-500 hover:bg-violet-900/40 transition-all">Create Deal</button>
                <button onClick={() => setCreateNotifBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg hover:text-white hover:border-violet-500 hover:bg-violet-900/40 transition-all">Create Push Notification</button>
                <button onClick={() => setCheckinQrBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg hover:text-white hover:border-violet-500 hover:bg-violet-900/40 transition-all">Check-in QR</button>
                <button onClick={() => setDeletingBusiness(biz)} className="px-3 py-1.5 text-xs font-medium text-red-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-red-300 hover:border-red-700 hover:bg-red-950/30 transition-all">Delete</button>
              </div>
            </div>
            <div className="sm:hidden flex flex-wrap gap-1.5 mt-2 ml-[52px]">
              <button onClick={() => handleCopyId(biz.id)} className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg active:bg-gray-700 transition-all">Copy ID</button>
              <button onClick={() => setEditingBusiness(biz)} className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg active:bg-gray-700 transition-all">Edit</button>
              <button onClick={() => setCreateEventBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg active:bg-violet-900/40 transition-all">Create Event</button>
              <button onClick={() => onRequestCreateDeal(biz, selectedEnv)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg active:bg-violet-900/40 transition-all">Create Deal</button>
              <button onClick={() => setCreateNotifBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg active:bg-violet-900/40 transition-all">Push Notif</button>
              <button onClick={() => setCheckinQrBiz(biz)} className="px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-950/30 border border-violet-800/50 rounded-lg active:bg-violet-900/40 transition-all">Check-in QR</button>
              <button onClick={() => setDeletingBusiness(biz)} className="px-3 py-1.5 text-xs font-medium text-red-400 bg-gray-800 border border-gray-700 rounded-lg active:bg-red-950/30 transition-all">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {!loading && total > 0 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + businesses.length, total)} of {total} businesses
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500 tabular-nums">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <Suspense fallback={null}>
          <CreateBusinessModal onClose={() => setShowCreateModal(false)} onCreated={handleCreated} />
        </Suspense>
      )}

      {editingBusiness && (
        <Suspense fallback={null}>
          <EditBusinessModal business={editingBusiness} onClose={() => setEditingBusiness(null)} onSaved={handleEdited} apiUrl={activeApiUrl} />
        </Suspense>
      )}

      {deletingBusiness && (
        <DeleteConfirmModal
          business={deletingBusiness}
          onClose={() => setDeletingBusiness(null)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {createNotifBiz && (
        <Suspense fallback={null}>
          <CreatePushNotificationModal
            business={createNotifBiz}
            env={selectedEnv}
            onClose={() => setCreateNotifBiz(null)}
            onSent={() => setCreateNotifBiz(null)}
          />
        </Suspense>
      )}

      {checkinQrBiz && (
        <Suspense fallback={null}>
          <CheckinQrModal business={checkinQrBiz} env={selectedEnv} onClose={() => setCheckinQrBiz(null)} />
        </Suspense>
      )}

      {createEventBiz && (
        <Suspense fallback={null}>
          <CreateEventModal
            business={createEventBiz}
            initialEnvironment={selectedEnv}
            onClose={() => setCreateEventBiz(null)}
            onCreated={() => {
              setCreateEventBiz(null);
              showSuccess(`Event created for ${createEventBiz.name}.`);
            }}
          />
        </Suspense>
      )}
    </>
  );
}
